import { useEffect, useState } from 'react';
import { MinusCircle } from 'lucide-react';
import MaskedInput from './MaskedInput';
import WizardDialog from './WizardDialog';
import { LOS_LABELS, type LosType } from '../types';
import { LOS_FIELDS, ENCOMPASS_SERVICE_SETUP_KEYS } from './losFields';
import {
  REMOVE_LOS_TITLE,
  REMOVE_LOS_BODY,
  REMOVE_LOS_CONFIRM,
  REMOVE_LOS_CANCEL,
  instanceTakenError,
  INSTANCE_TAKEN_TITLE,
  INSTANCE_TAKEN_DISMISS,
  instanceTakenBody,
  lenderCodeTakenError,
} from '../copy';
import { checkInstanceIdAvailable, checkMlLenderCodeAvailable } from '../../../../api/lenderOnboarding';
import { trimOnBlur } from '../trimOnBlur';

interface Props {
  index: number;
  losType: LosType | '';
  credentials: Record<string, string>;
  available: LosType[];
  removeLabel: string;
  onSelect: (t: LosType) => void;
  onChange: (c: Record<string, string>) => void;
  onRemove: () => void;
  /** The card's verdict, lifted so the step's Next gate can hold a taken instance id. */
  onBlockedChange?: (blocked: boolean) => void;
}

export default function LosIntegrationCard({
  index, losType, credentials, available, removeLabel, onSelect, onChange, onRemove, onBlockedChange,
}: Props) {
  const [confirming, setConfirming] = useState(false);

  // OM-3918 AC5: an instance id that already belongs to a lender stops the wizard — this flow
  // creates new accounts only; changes to existing ones are for Account Maintenance. Checked as
  // it is typed, like the Salesforce id and email probes.
  const [instanceTaken, setInstanceTaken] = useState<{ lenderName: string } | null>(null);
  const [checkedInstanceId, setCheckedInstanceId] = useState<string | null>(null);
  const [showTakenDialog, setShowTakenDialog] = useState(false);

  const instanceId = losType === 'encompass' ? (credentials.instanceId ?? '').trim() : '';

  // OM-3918: the ML Lender Codes are unique per lender too. Same probe cadence, but inline
  // only — no dialog: unlike the instance id, no AC prescribes one.
  const mlLos = losType === 'ml-consumer' || losType === 'ml-mortgage' ? losType : null;
  const lenderCode = mlLos ? (credentials.lenderCode ?? '').trim() : '';
  const [codeTaken, setCodeTaken] = useState<{ lenderName: string } | null>(null);
  const [checkedLenderCode, setCheckedLenderCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!mlLos || !lenderCode) {
      setCodeTaken(null);
      setCheckedLenderCode(null);
      return;
    }
    checkMlLenderCodeAvailable(mlLos, lenderCode).then((verdict) => {
      if (cancelled) return;
      setCheckedLenderCode(lenderCode);
      setCodeTaken(verdict.available ? null : { lenderName: verdict.lenderName ?? lenderCode });
    });
    return () => { cancelled = true; };
  }, [mlLos, lenderCode]);

  useEffect(() => {
    let cancelled = false;
    if (!instanceId) {
      setInstanceTaken(null);
      setCheckedInstanceId(null);
      return;
    }
    checkInstanceIdAvailable(instanceId).then((verdict) => {
      if (cancelled) return;
      setCheckedInstanceId(instanceId);
      if (verdict.available) {
        setInstanceTaken(null);
      } else {
        setInstanceTaken({ lenderName: verdict.lenderName ?? instanceId });
        setShowTakenDialog(true);
      }
    });
    return () => { cancelled = true; };
  }, [instanceId]);

  // Same guard as the other probes: blocked from the instant a value is typed until its verdict
  // lands, so Next can never slip through the in-flight window.
  const awaitingCheck = !!instanceId && checkedInstanceId !== instanceId;
  const awaitingCodeCheck = !!lenderCode && checkedLenderCode !== lenderCode;
  const blocked =
    (losType === 'encompass' && (!!instanceTaken || awaitingCheck)) ||
    (!!mlLos && (!!codeTaken || awaitingCodeCheck));
  useEffect(() => {
    onBlockedChange?.(blocked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked]);
  const fields = losType ? LOS_FIELDS[losType] : [];
  const main = fields.filter((f) => !ENCOMPASS_SERVICE_SETUP_KEYS.includes(f.key));
  const setup = fields.filter((f) => ENCOMPASS_SERVICE_SETUP_KEYS.includes(f.key));

  // Every field of a chosen LOS is required — the step's Next gate holds until all are filled,
  // so each one carries the marker that says why.
  const renderField = (f: { key: string; label: string; masked?: boolean }) =>
    f.masked ? (
      <MaskedInput key={f.key} id={`los-${index}-${f.key}`} label={f.label} required
        value={credentials[f.key] ?? ''} onChange={(v) => onChange({ [f.key]: v })} />
    ) : (
      // The Instance ID gets its own full row, like the design.
      <div key={f.key} className={f.key === 'instanceId' ? 'field sm:col-span-2' : 'field'}>
        <label className="label" htmlFor={`los-${index}-${f.key}`}>
          {f.label} <span className="text-red-500">*</span>
        </label>
        {/* These credentials have no schema of their own to hang a rule on, so the trim lives
            here, on blur, like the schema-backed boxes of Steps 1 and 4 (OM-4602). */}
        <input id={`los-${index}-${f.key}`} className="input" value={credentials[f.key] ?? ''}
          onChange={(e) => onChange({ [f.key]: e.target.value })}
          onBlur={trimOnBlur((value) => onChange({ [f.key]: value }))} />
        {f.key === 'instanceId' && instanceTaken && (
          <p className="text-sm text-red-500 mt-1">{instanceTakenError(instanceTaken.lenderName)}</p>
        )}
        {f.key === 'lenderCode' && codeTaken && (
          <p className="text-sm text-red-500 mt-1">{lenderCodeTakenError(codeTaken.lenderName)}</p>
        )}
      </div>
    );

  return (
    <div className="field mt-4">
      <label className="label" htmlFor={`los-select-${index}`}>LOS Integration</label>
      <select id={`los-select-${index}`} className="input" value={losType}
        onChange={(e) => onSelect(e.target.value as LosType)}>
        <option value="">Select an LOS</option>
        {available.map((t) => <option key={t} value={t}>{LOS_LABELS[t]}</option>)}
      </select>

      {losType && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 mt-4">{main.map(renderField)}</div>
          {setup.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Service Setup IDs</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Enter the Service ID for each Service the organization uses.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">{setup.map(renderField)}</div>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button type="button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:underline dark:text-red-500"
              onClick={() => setConfirming(true)}>
              <MinusCircle className="h-4 w-4" aria-hidden="true" /> {removeLabel}
            </button>
          </div>
        </>
      )}

      <WizardDialog
        open={showTakenDialog}
        title={INSTANCE_TAKEN_TITLE}
        body={instanceTakenBody(instanceTaken?.lenderName ?? '')}
        confirmLabel={INSTANCE_TAKEN_DISMISS}
        // Closing clears everything typed into the card, as removing an LOS does, but keeps the
        // LOS selected.
        onConfirm={() => {
          setShowTakenDialog(false);
          onChange(Object.fromEntries(fields.map((f) => [f.key, ''])));
        }}
      />

      <WizardDialog open={confirming} title={REMOVE_LOS_TITLE} body={REMOVE_LOS_BODY}
        confirmLabel={REMOVE_LOS_CONFIRM} cancelLabel={REMOVE_LOS_CANCEL}
        onConfirm={() => { setConfirming(false); onRemove(); }}
        onCancel={() => setConfirming(false)} />
    </div>
  );
}
